/* QuizV2 — A/B test variant of the quiz with redesigned flow:
 * Q1 (hybrid) → Opt-in (firstName + email only) → Q2..Q6 → Q7 qualifier → submit.
 *
 * Maps v2 answers into v1's answer1..answer7 slots via a deterministic mapping:
 *   v1 answer1 (background)         ← v2 Q2 (surprising background)
 *   v1 answer2 (superpower)         ← v2 Q3 chips (joined)
 *   v1 answer3 (method)             ← v2 Q3 text field
 *   v1 answer4 (story)              ← v2 Q4
 *   v1 answer5 (the ONE thing)      ← v2 Q6
 *   v1 answer6 (work / ideal client)← `${v2 Q1 chip} for ${v2 Q1 audience text}`
 *   v1 answer7 (OUTCOME FILTER)     ← v2 Q5 transformation (chips + text) — CRITICAL
 *   investment_stage                ← v2 Q7 qualifier (cold/warm/hot)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import './QuizV2.css';

const PROFESSION_CHIPS = ['Coach', 'Consultant', 'Real estate pro', 'Speaker / Author', 'Service provider', 'Founder / Operator', 'Other'];
const SUPERPOWER_CHIPS = ['Simplifying complexity', 'Reading people', 'Seeing patterns others miss', 'Telling stories', 'Building trust fast', 'Making the hard thing simple', 'Other'];
const TRANSFORMATION_CHIPS = ['They finally get clarity', 'They start charging more', 'They get seen / noticed', 'They stop blending in', 'They grow faster', 'They feel confident', 'Other'];
const QUALIFIER_OPTIONS = [
  { value: 'cold', label: 'Just starting to explore', sub: "I'm curious but haven't committed to investing yet." },
  { value: 'warm', label: 'Actively working on it', sub: "I'm putting in effort but need better positioning." },
  { value: 'hot',  label: 'Actively investing', sub: "I'm ready to invest in getting this right and moving fast." },
];

const STEPS = [
  'q1_profession',   // 1
  'opt_in',          // 2
  'q2_background',   // 3
  'q3_superpower',   // 4
  'q4_story',        // 5
  'q5_transformation', // 6
  'q6_one_thing',    // 7
  'q7_qualifier',    // 8
];
const TOTAL_STEPS = STEPS.length;
const STORAGE_KEY = 'fyo_quiz_v2_draft';

const validateEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

function AutoGrowTextarea({ value, onChange, placeholder, testid, minRows = 4 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.max(el.scrollHeight, minRows * 24) + 'px';
  }, [value, minRows]);
  return (
    <textarea
      ref={ref}
      className="qv2-textarea"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      rows={minRows}
    />
  );
}

function Chip({ label, selected, onClick, testid }) {
  return (
    <button type="button" className={`qv2-chip${selected ? ' selected' : ''}`} onClick={onClick} data-testid={testid}>
      {label}
    </button>
  );
}

export default function QuizV2({ onOptIn, onComplete, onTrack }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const [errors, setErrors] = useState({});

  // V2 quiz state
  const [profession, setProfession] = useState('');         // chip
  const [professionOther, setProfessionOther] = useState(''); // if chip === 'Other'
  const [audience, setAudience] = useState('');             // text after "...for"

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');

  const [background, setBackground] = useState('');         // Q2 open text
  const [superpowerChips, setSuperpowerChips] = useState([]); // Q3 chips
  const [superpowerOther, setSuperpowerOther] = useState('');
  const [methodText, setMethodText] = useState('');         // Q3 text
  const [story, setStory] = useState('');                   // Q4 open text
  const [transformChips, setTransformChips] = useState([]); // Q5 chips
  const [transformOther, setTransformOther] = useState('');
  const [transformText, setTransformText] = useState('');   // Q5 text
  const [oneThing, setOneThing] = useState('');             // Q6 open text
  const [qualifier, setQualifier] = useState('');           // Q7

  const stepKey = STEPS[stepIdx];
  const stepNumber = stepIdx + 1;
  const minLeft = Math.max(1, Math.round((TOTAL_STEPS - stepIdx) * 0.9));
  const progressPct = Math.round(((stepIdx) / (TOTAL_STEPS - 1)) * 100);

  // ── Persistence (localStorage) ──
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.profession) setProfession(d.profession);
      if (d.professionOther) setProfessionOther(d.professionOther);
      if (d.audience) setAudience(d.audience);
      if (d.firstName) setFirstName(d.firstName);
      if (d.email) setEmail(d.email);
      if (d.background) setBackground(d.background);
      if (d.superpowerChips) setSuperpowerChips(d.superpowerChips);
      if (d.superpowerOther) setSuperpowerOther(d.superpowerOther);
      if (d.methodText) setMethodText(d.methodText);
      if (d.story) setStory(d.story);
      if (d.transformChips) setTransformChips(d.transformChips);
      if (d.transformOther) setTransformOther(d.transformOther);
      if (d.transformText) setTransformText(d.transformText);
      if (d.oneThing) setOneThing(d.oneThing);
      if (d.qualifier) setQualifier(d.qualifier);
      if (typeof d.stepIdx === 'number' && d.stepIdx < STEPS.length) setStepIdx(d.stepIdx);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    const draft = { stepIdx, profession, professionOther, audience, firstName, email, background, superpowerChips, superpowerOther, methodText, story, transformChips, transformOther, transformText, oneThing, qualifier };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(draft)); } catch (e) { /* ignore */ }
  }, [stepIdx, profession, professionOther, audience, firstName, email, background, superpowerChips, superpowerOther, methodText, story, transformChips, transformOther, transformText, oneThing, qualifier]);

  // ── Helpers ──
  const resolvedProfession = profession === 'Other' ? (professionOther.trim() || 'Other') : profession;
  const resolvedSuperpowers = useMemo(() => {
    const chips = (superpowerChips || []).filter((c) => c !== 'Other');
    const withOther = superpowerChips.includes('Other') && superpowerOther.trim() ? [...chips, superpowerOther.trim()] : chips;
    return withOther;
  }, [superpowerChips, superpowerOther]);
  const resolvedTransform = useMemo(() => {
    const chips = (transformChips || []).filter((c) => c !== 'Other');
    const withOther = transformChips.includes('Other') && transformOther.trim() ? [...chips, transformOther.trim()] : chips;
    return withOther;
  }, [transformChips, transformOther]);

  const toggleChip = (list, setList, value) => {
    setList((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= 2) return [...prev.slice(1), value]; // cap at 2
      return [...prev, value];
    });
  };

  // ── Validation per step ──
  const canAdvance = useMemo(() => {
    switch (stepKey) {
      case 'q1_profession':
        if (!profession) return false;
        if (profession === 'Other' && !professionOther.trim()) return false;
        if (!audience.trim()) return false;
        return true;
      case 'opt_in':
        return firstName.trim().length > 0 && validateEmail(email);
      case 'q2_background':
        return background.trim().length >= 1;
      case 'q3_superpower':
        return resolvedSuperpowers.length > 0 && methodText.trim().length >= 1;
      case 'q4_story':
        return story.trim().length >= 1;
      case 'q5_transformation':
        return resolvedTransform.length > 0 && transformText.trim().length >= 1;
      case 'q6_one_thing':
        return oneThing.trim().length >= 1;
      case 'q7_qualifier':
        return ['cold', 'warm', 'hot'].includes(qualifier);
      default: return false;
    }
  }, [stepKey, profession, professionOther, audience, firstName, email, background, resolvedSuperpowers, methodText, story, resolvedTransform, transformText, oneThing, qualifier]);

  // ── Step handlers ──
  const handleNext = async () => {
    if (!canAdvance || submitting) return;
    setErrors({});

    // Step 2 = opt-in → fire opt-in callback (Make.com + GHL via App.js)
    if (stepKey === 'opt_in') {
      try {
        setSubmitting(true);
        await Promise.resolve(onOptIn && onOptIn({ firstName: firstName.trim(), email: email.trim() }));
        onTrack && onTrack('quiz_v2_optin_submitted');
      } finally {
        setSubmitting(false);
      }
    }

    // Final step → compose payload and submit
    if (stepKey === 'q7_qualifier') {
      const answer6 = `${resolvedProfession} for ${audience.trim()}`.trim();
      const superpowersStr = resolvedSuperpowers.join(', ');
      const transformChipsStr = resolvedTransform.join(', ');
      const answer7 = transformChipsStr
        ? `${transformChipsStr}. ${transformText.trim()}`
        : transformText.trim();

      const payload = {
        firstName: firstName.trim(),
        email: email.trim(),
        phone: '',  // v2 does not collect phone
        answer1: background.trim(),               // Unexpected Background
        answer2: superpowersStr,                  // Superpower (chips joined)
        answer3: methodText.trim(),               // Unique problem-solving / method (user's own words)
        answer4: story.trim(),                    // Memorable Story
        answer5: oneThing.trim(),                 // The ONE Thing
        answer6: answer6,                          // Work & Ideal Client (composed)
        answer7: answer7,                          // 🎯 OUTCOME FILTER ← v2 transformation
        investment_stage: qualifier,
      };
      setSubmitting(true);
      onTrack && onTrack('quiz_v2_complete', { investment_stage: qualifier });
      try {
        await Promise.resolve(onComplete && onComplete(payload));
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const nextIdx = stepIdx + 1;
    // Show milestone toast when entering halfway point (after step 4 — Q4 story)
    if (nextIdx === 4) {
      setShowMilestone(true);
      setTimeout(() => setShowMilestone(false), 3500);
    }
    setStepIdx(nextIdx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onTrack && onTrack('quiz_v2_step_complete', { step: stepNumber });
  };

  const handleBack = () => {
    if (stepIdx === 0 || submitting) return;
    setStepIdx(stepIdx - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Render screens ──
  const renderStep = () => {
    switch (stepKey) {
      case 'q1_profession':
        return (
          <>
            <div className="qv2-eyebrow">Step 1 of 8</div>
            <h2 className="qv2-heading">What do you do, and who do you do your best work with?</h2>
            <p className="qv2-helper">One tap, then a few words.</p>
            <div className="qv2-chip-grid" data-testid="qv2-profession-chips">
              {PROFESSION_CHIPS.map((c) => (
                <Chip key={c} label={c} selected={profession === c} onClick={() => setProfession(c)} testid={`qv2-prof-chip-${c.toLowerCase().replace(/\s+|[/]/g, '-')}`} />
              ))}
            </div>
            {profession === 'Other' && (
              <input
                className="qv2-input-plain" style={{ marginBottom: 16 }} placeholder="What do you do?"
                value={professionOther} onChange={(e) => setProfessionOther(e.target.value)}
                data-testid="qv2-profession-other-input"
              />
            )}
            <div className="qv2-input-row">
              <span className="qv2-input-prefix">…for</span>
              <input
                className="qv2-input" type="text"
                placeholder="overwhelmed founders, women in midlife, etc."
                value={audience} onChange={(e) => setAudience(e.target.value)}
                data-testid="qv2-audience-input"
              />
            </div>
          </>
        );

      case 'opt_in':
        return (
          <>
            <div className="qv2-eyebrow">Step 2 of 8 · Save your progress</div>
            <h2 className="qv2-heading">You're on your way. Where should we send your ONLY Statement?</h2>
            <p className="qv2-helper">We'll save your progress and email you a link so you can finish anytime.</p>
            <label className="qv2-field-label">First name</label>
            <input className="qv2-input-plain" style={{ marginBottom: 14 }} type="text" placeholder="Your first name"
                   value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="qv2-optin-firstName" />
            <label className="qv2-field-label">Email</label>
            <input className="qv2-input-plain" type="email" placeholder="you@example.com"
                   value={email} onChange={(e) => setEmail(e.target.value)} data-testid="qv2-optin-email" />
            {errors.optin && <div className="qv2-err">{errors.optin}</div>}
          </>
        );

      case 'q2_background':
        return (
          <>
            <div className="qv2-eyebrow">Step 3 of 8</div>
            <h2 className="qv2-heading">What's the most surprising or unexpected part of your background that shapes how you work?</h2>
            <p className="qv2-helper">Think about what you've done that most people in your industry haven't.</p>
            <AutoGrowTextarea value={background} onChange={setBackground} placeholder="e.g., I was a firefighter before becoming a business coach." testid="qv2-background-input" />
          </>
        );

      case 'q3_superpower':
        return (
          <>
            <div className="qv2-eyebrow">Step 4 of 8</div>
            <h2 className="qv2-heading">What comes easily to you but seems hard or unusual to others?</h2>
            <p className="qv2-helper">The thing people thank you for. Tap what fits, then make it yours.</p>
            <div className="qv2-chip-grid" data-testid="qv2-superpower-chips">
              {SUPERPOWER_CHIPS.map((c) => (
                <Chip key={c} label={c} selected={superpowerChips.includes(c)}
                      onClick={() => toggleChip(superpowerChips, setSuperpowerChips, c)}
                      testid={`qv2-sp-chip-${c.toLowerCase().replace(/\s+|[/]/g, '-')}`} />
              ))}
            </div>
            {superpowerChips.includes('Other') && (
              <input className="qv2-input-plain" style={{ marginBottom: 16 }} placeholder="What else?"
                     value={superpowerOther} onChange={(e) => setSuperpowerOther(e.target.value)}
                     data-testid="qv2-sp-other-input" />
            )}
            <label className="qv2-field-label">Say it in your words:</label>
            <input className="qv2-input-plain" type="text" placeholder="e.g., I find the one detail that makes someone unforgettable."
                   value={methodText} onChange={(e) => setMethodText(e.target.value)} data-testid="qv2-method-input" />
          </>
        );

      case 'q4_story':
        return (
          <>
            <div className="qv2-eyebrow">Step 5 of 8</div>
            <h2 className="qv2-heading">What's one story from your life or career that captures something essential about who you are?</h2>
            <p className="qv2-helper">The memorable moment that makes you unforgettable. A few sentences.</p>
            <AutoGrowTextarea value={story} onChange={setStory} placeholder="e.g., My grandmother gave me 50 two-dollar bills to carry on trips and do a good deed." testid="qv2-story-input" minRows={5} />
          </>
        );

      case 'q5_transformation':
        return (
          <>
            <div className="qv2-eyebrow">Step 6 of 8 · The transformation</div>
            <h2 className="qv2-heading">When your best clients work with you, what actually changes for them?</h2>
            <p className="qv2-helper">Not what you do — what they walk away with.</p>
            <div className="qv2-chip-grid" data-testid="qv2-transform-chips">
              {TRANSFORMATION_CHIPS.map((c) => (
                <Chip key={c} label={c} selected={transformChips.includes(c)}
                      onClick={() => toggleChip(transformChips, setTransformChips, c)}
                      testid={`qv2-tf-chip-${c.toLowerCase().replace(/\s+|[/]/g, '-')}`} />
              ))}
            </div>
            {transformChips.includes('Other') && (
              <input className="qv2-input-plain" style={{ marginBottom: 16 }} placeholder="What other change?"
                     value={transformOther} onChange={(e) => setTransformOther(e.target.value)}
                     data-testid="qv2-tf-other-input" />
            )}
            <label className="qv2-field-label">The real before-to-after:</label>
            <input className="qv2-input-plain" type="text" placeholder="e.g., they go from invisible to the only obvious choice."
                   value={transformText} onChange={(e) => setTransformText(e.target.value)} data-testid="qv2-transform-input" />
          </>
        );

      case 'q6_one_thing':
        return (
          <>
            <div className="qv2-eyebrow">Step 7 of 8</div>
            <h2 className="qv2-heading">If you could only be known for ONE thing, what would it be?</h2>
            <p className="qv2-helper">You can't be everything. What's the ONE thing?</p>
            <AutoGrowTextarea value={oneThing} onChange={setOneThing} placeholder="e.g., The person who turns invisible experts into category leaders." testid="qv2-onething-input" minRows={4} />
          </>
        );

      case 'q7_qualifier':
        return (
          <>
            <div className="qv2-eyebrow">Step 8 of 8 · One more thing</div>
            <h2 className="qv2-heading">When it comes to building your authority and personal brand, where are you right now?</h2>
            <p className="qv2-helper">Pick the one that fits — there's no wrong answer.</p>
            <div className="qv2-q-list" data-testid="qv2-qualifier-list">
              {QUALIFIER_OPTIONS.map((o) => (
                <div key={o.value} className={`qv2-q-opt${qualifier === o.value ? ' selected' : ''}`}
                     onClick={() => setQualifier(o.value)} data-testid={`qv2-qualifier-${o.value}`}>
                  <div className="qv2-q-radio"></div>
                  <div className="qv2-q-text"><strong>{o.label}</strong>{o.sub}</div>
                </div>
              ))}
            </div>
          </>
        );

      default: return null;
    }
  };

  const ctaLabel = stepKey === 'opt_in' ? 'Continue' : stepKey === 'q7_qualifier' ? 'Generate My ONLY Statement' : 'Next';

  return (
    <div className="qv2-root" data-testid="quiz-v2-root">
      <div className="qv2-progress">
        <div className="qv2-prog-meta">
          <span data-testid="qv2-progress-step">Step <strong>{stepNumber}</strong> of {TOTAL_STEPS}</span>
          <span data-testid="qv2-progress-mins">~{minLeft} min left</span>
        </div>
        <div className="qv2-prog-track"><div className="qv2-prog-fill" style={{ width: `${progressPct}%` }} data-testid="qv2-progress-bar"></div></div>
        <div className="qv2-autosave">Your progress is saved — we'll email you a link to pick up where you left off</div>
      </div>

      {showMilestone && (
        <div className="qv2-milestone" data-testid="qv2-milestone"><span className="dot"></span><span>You're halfway there.</span></div>
      )}

      <div className="qv2-card">
        {renderStep()}
        <div className="qv2-actions">
          <button type="button" className="qv2-back" onClick={handleBack} disabled={stepIdx === 0 || submitting} data-testid="qv2-back-btn">← Back</button>
          <button type="button" className="qv2-next" onClick={handleNext} disabled={!canAdvance || submitting} data-testid="qv2-next-btn">
            {submitting ? 'Working…' : (<>{ctaLabel}<span className="arrow">→</span></>)}
          </button>
        </div>
      </div>
    </div>
  );
}
